import { useCallback } from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';

import { Button, SideSectionCard, Text, TextProps, spacing } from '@pletly/ui';
import { TeamInfo, TeamInfoProps } from '../TeamInfo';
import { EmptyTeams } from './EmptyTeams';
import { withTranslation } from '../../../locales/hoc';

type Props = {
	organisationUuid: string;
	teams: TeamInfoProps['team'][];
	variant?: 'all' | 'other-only';
	withCreateButton?: boolean;
	style?: StyleProp<ViewStyle>;
	titleCategory?: TextProps['category'];
	activeTeam?: TeamInfoProps['team'];
};

export const TeamsCard = withTranslation(
	({
		organisationUuid,
		teams,
		variant = 'all',
		withCreateButton = true,
		style,
		titleCategory = 'h4',
		activeTeam,
	}: Props) => {
		const { t } = useTranslation();
		const { navigate } = useNavigation();
		const handleCreatePress = useCallback(() => {
			navigate('NewTeam', { organisationUuid });
		}, [navigate, organisationUuid]);

		const title = variant === 'other-only' ? t('teams.other-teams') : t('teams.teams');
		const emptyText =
			variant === 'other-only' ? t('teams.no-other-teams-created') : t('teams.no-teams-yet');

		return (
			<SideSectionCard style={[styles.card, style]} helpText={t('teams.help-content')}>
				<View style={styles.header}>
					<Text category={titleCategory}>{title}</Text>
					{withCreateButton && (
						<Button variant="outline" color="primary" size="small" onPress={handleCreatePress}>
							{t('common.create-new')}
						</Button>
					)}
				</View>
				{teams.length > 0 ? (
					teams
						.filter((team) => !team.parentTeam)
						.map((team) => (
							<TeamInfo key={`team-${team.uuid}`} team={team} activeUuid={activeTeam?.uuid} />
						))
				) : (
					<EmptyTeams organisationUuid={organisationUuid} text={emptyText} />
				)}
			</SideSectionCard>
		);
	},
);

const styles = StyleSheet.create({
	card: {
		//@ts-expect-error valid on web
		flex: 'unset',
		alignSelf: 'flex-start',
		gap: spacing(2),
	},
	header: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
	},
});
